import { useState } from 'react';
import { Alert, Card, Col, Flex, Row, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { NoData } from '../NoData';
import { StudyYearDropdown } from '../StudyYearDropdown';
import { RollbackStudentList } from './RollbackStudentList';
import { RollbackProcessViewer } from './RollbackProcessViewer';
import { useCurrentStudyYear } from '../../hooks/useStudyYear';
import type { RollbackStudent } from '../../Types/interfaces';

export const ProcessRollbackComponent = () => {
  const { t } = useTranslation();
  const { data: currentStudyYear, isLoading: isLoadingCurrentStudyYear } =
    useCurrentStudyYear();
  // undefined until the admin selects a study year, then the dropdown shows the current one
  const [selectedStudyYearId, setSelectedStudyYearId] = useState<number>();
  const [selectedStudent, setSelectedStudent] = useState<RollbackStudent>();
  const [isStudentListCollapsed, setIsStudentListCollapsed] = useState(false);

  // the dropdown shows the current study year until another one is selected, the same is listed here
  const shownStudyYearId = selectedStudyYearId ?? currentStudyYear?.id;

  if (isLoadingCurrentStudyYear) {
    return <Spin />;
  }

  if (shownStudyYearId === undefined) {
    return (
      <Alert type="info" showIcon message={t('academicCalendar.noStudyYear')} />
    );
  }

  return (
    <>
      <Flex align="center" gap={8} wrap style={{ marginBottom: 16 }}>
        <Typography.Text strong>{t('studyYear.label')}:</Typography.Text>
        <StudyYearDropdown
          value={selectedStudyYearId}
          onChange={(studyYearId) => {
            setSelectedStudyYearId(studyYearId);
            // the selected student may have no processes in the other study year
            setSelectedStudent(undefined);
          }}
        />
      </Flex>

      {/* collapsed: the student list is a narrow column of avatars, the process viewer gets the rest of the width */}
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={isStudentListCollapsed ? { flex: '72px' } : 8}>
          <RollbackStudentList
            studyYearId={shownStudyYearId}
            selectedStudentId={selectedStudent?.id}
            onSelect={setSelectedStudent}
            collapsed={isStudentListCollapsed}
            onToggleCollapsed={() =>
              setIsStudentListCollapsed((collapsed) => !collapsed)
            }
          />
        </Col>
        <Col
          xs={24}
          lg={isStudentListCollapsed ? { flex: '1 1 0' } : 16}
          style={{ minWidth: 0 }}
        >
          <Card>
            {selectedStudent ? (
              <RollbackProcessViewer
                key={selectedStudent.id}
                student={selectedStudent}
              />
            ) : (
              <NoData message={t('processRollback.selectStudent')} />
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
};
